import {
  TEAM_ROLE_LABEL,
  TEAM_ROLES,
  type MeResponse,
  type PasswordResetLink,
  type TeamInvite,
  type TeamMember,
  type TeamRole,
  type TeamSummary,
} from '@greenroom/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, LogOut, Pencil, Trash2, UserPlus, X } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '../components/ui/button'
import { ConfirmDialog, Dialog } from '../components/ui/dialog'
import { Field } from '../components/ui/field'
import { Badge, CopyButton, ErrorNote, PageHeader, Section } from '../components/ui/misc'
import { Select } from '../components/ui/select'
import { FullPageSpinner } from '../components/ui/spinner'
import { Tooltip } from '../components/ui/tooltip'
import { api, errorMessage } from '../lib/api'
import { meQuery, useCurrentTeam, useUser } from '../lib/auth'
import { formatDate, formatRelative, plural } from '../lib/format'
import { q } from '../lib/queries'
import { useTitle } from '../lib/title'
import { QueryError } from './errors'

const ROLE_HINT: Record<TeamRole, string> = {
  owner: 'Everything, including members, invites and deleting studios',
  producer: 'Runs sessions, creates studios, edits and renders recordings',
  viewer: 'Joins studios as a guest, watches and downloads recordings',
}
const ROLE_OPTIONS = [...TEAM_ROLES].reverse().map((r) => ({ value: r, label: TEAM_ROLE_LABEL[r] }))

/** Applies a change to the signed-in user's own team list, so the switcher follows without a refetch. */
function useMeTeams() {
  const qc = useQueryClient()
  return (fn: (teams: TeamSummary[]) => TeamSummary[]) =>
    qc.setQueryData(meQuery.queryKey, (me: MeResponse | null | undefined) => (me ? { ...me, teams: fn(me.teams) } : me))
}

export function TeamPage() {
  const { team } = useCurrentTeam()
  const me = useUser()
  useTitle('Team', team.name)
  const members = useQuery(q.members(team.id))
  const isOwner = team.role === 'owner'
  const [renaming, setRenaming] = useState(false)

  return (
    <>
      <PageHeader
        title={team.name}
        subtitle={members.data ? plural(members.data.length, 'member') : 'Members and their roles'}
        actions={
          isOwner && (
            <Button icon={<Pencil className="size-4" />} onClick={() => setRenaming(true)}>
              Rename
            </Button>
          )
        }
      />
      <div className="grid gap-6">
        {members.isPending ? (
          <FullPageSpinner />
        ) : members.isError ? (
          <QueryError error={members.error} retry={members.refetch} what="team" />
        ) : (
          <MemberList team={team} members={members.data} meId={me.user.id} />
        )}
        {isOwner && <Invites team={team} />}
        <Section title="Roles">
          <dl className="grid gap-3 text-sm sm:grid-cols-3">
            {[...TEAM_ROLES].reverse().map((r) => (
              <div key={r}>
                <dt className="font-medium">{TEAM_ROLE_LABEL[r]}</dt>
                <dd className="mt-0.5 text-muted">{ROLE_HINT[r]}</dd>
              </div>
            ))}
          </dl>
        </Section>
      </div>
      {isOwner && <RenameTeamDialog team={team} open={renaming} onOpenChange={setRenaming} />}
    </>
  )
}

function MemberList({ team, members, meId }: { team: TeamSummary; members: TeamMember[]; meId: string }) {
  const qc = useQueryClient()
  const updateMeTeams = useMeTeams()
  const navigate = useNavigate()
  const isOwner = team.role === 'owner'
  const [removing, setRemoving] = useState<TeamMember | null>(null)
  const [resetting, setResetting] = useState<TeamMember | null>(null)
  const key = q.members(team.id).queryKey

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: TeamRole }) =>
      api(`/api/teams/${team.id}/members/${userId}`, { method: 'PATCH', body: { role } }),
    onSuccess: (_res, { userId, role }) => {
      qc.setQueryData(key, (list) => list?.map((m) => (m.userId === userId ? { ...m, role } : m)))
      if (userId === meId) updateMeTeams((teams) => teams.map((t) => (t.id === team.id ? { ...t, role } : t)))
    },
  })
  const remove = useMutation({
    mutationFn: (userId: string) => api(`/api/teams/${team.id}/members/${userId}`, { method: 'DELETE' }),
    meta: { silent: true },
    onSuccess: (_res, userId) => {
      if (userId === meId) {
        updateMeTeams((teams) => teams.filter((t) => t.id !== team.id))
        navigate('/studios', { replace: true })
        return
      }
      qc.setQueryData(key, (list) => list?.filter((m) => m.userId !== userId))
    },
  })

  return (
    <Section title="Members">
      <ul className="-mx-2 divide-y divide-border">
        {members.map((m) => {
          const self = m.userId === meId
          return (
            <li key={m.userId} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-2 py-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-surface-3 text-xs font-semibold uppercase">
                {m.name.slice(0, 1)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 truncate text-sm font-medium">
                  {m.name}
                  {self && <Badge>You</Badge>}
                </div>
                <div className="truncate text-xs text-faint">
                  {m.email} · joined {formatDate(m.joinedAt)}
                </div>
              </div>
              {isOwner ? (
                <Select
                  label={`Role of ${m.name}`}
                  className="h-9 w-36"
                  value={m.role}
                  options={ROLE_OPTIONS}
                  onChange={(role) => role !== m.role && changeRole.mutate({ userId: m.userId, role })}
                />
              ) : (
                <Badge tone={m.role === 'owner' ? 'accent' : 'neutral'}>{TEAM_ROLE_LABEL[m.role]}</Badge>
              )}
              {self ? (
                <Tooltip label="Leave team">
                  <Button size="icon" variant="ghost" aria-label="Leave team" onClick={() => setRemoving(m)}>
                    <LogOut className="size-4" />
                  </Button>
                </Tooltip>
              ) : (
                isOwner && (
                  <span className="flex">
                    <Tooltip label={`Reset password of ${m.name}`}>
                      <Button size="icon" variant="ghost" aria-label={`Reset password of ${m.name}`} onClick={() => setResetting(m)}>
                        <KeyRound className="size-4" />
                      </Button>
                    </Tooltip>
                    <Tooltip label={`Remove ${m.name}`}>
                      <Button size="icon" variant="ghost" aria-label={`Remove ${m.name}`} onClick={() => setRemoving(m)}>
                        <Trash2 className="size-4" />
                      </Button>
                    </Tooltip>
                  </span>
                )
              )}
            </li>
          )
        })}
      </ul>
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={removing?.userId === meId ? `Leave ${team.name}?` : `Remove ${removing?.name}?`}
        description={
          removing?.userId === meId
            ? 'You lose access to its studios and recordings until someone invites you again.'
            : 'They lose access to the studios and recordings of this team right away.'
        }
        confirmLabel={removing?.userId === meId ? 'Leave team' : 'Remove'}
        destructive
        onConfirm={() => remove.mutateAsync(removing!.userId)}
      />
      {isOwner && <ResetPasswordDialog team={team} member={resetting} onClose={() => setResetting(null)} />}
    </Section>
  )
}

/** There is no email, so the owner creates the link and hands it over; it works once, for 24 hours. */
function ResetPasswordDialog({ team, member, onClose }: { team: TeamSummary; member: TeamMember | null; onClose: () => void }) {
  const [link, setLink] = useState<PasswordResetLink | null>(null)
  const create = useMutation({
    mutationFn: (userId: string) => api<PasswordResetLink>(`/api/teams/${team.id}/members/${userId}/password-reset`, { method: 'POST' }),
    meta: { silent: true },
    onSuccess: setLink,
  })
  const close = () => {
    onClose()
    setLink(null)
    create.reset()
  }
  const name = member?.name
  return (
    <Dialog
      open={member !== null}
      onOpenChange={(o) => !o && close()}
      title={`Reset password of ${name}`}
      description={`${name} sets a new password with the link. It works once, expires after 24 hours and signs them out on every device.`}
    >
      {link ? (
        <div className="space-y-4">
          <p className="text-sm text-muted">Send this link to {name}. It is shown only now; a new link replaces it.</p>
          <div className="flex gap-2">
            <input readOnly value={link.url} className="input font-mono text-[13px]" onFocus={(e) => e.currentTarget.select()} aria-label="Reset link" autoFocus />
            <CopyButton text={link.url} what="Reset link" />
          </div>
          <div className="flex justify-end">
            <Button variant="primary" onClick={close}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-muted">Their current password keeps working until the link is used.</p>
          {create.error && <ErrorNote>{errorMessage(create.error)}</ErrorNote>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button variant="primary" loading={create.isPending} onClick={() => member && create.mutate(member.userId)}>
              Create link
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  )
}

function Invites({ team }: { team: TeamSummary }) {
  const qc = useQueryClient()
  const invites = useQuery(q.invites(team.id))
  const [creating, setCreating] = useState(false)
  const key = q.invites(team.id).queryKey
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/teams/${team.id}/invites/${id}`, { method: 'DELETE' }),
    onSuccess: (_res, id) => qc.setQueryData(key, (list) => list?.filter((i) => i.id !== id)),
  })
  return (
    <Section
      title="Invite links"
      actions={
        <Button size="sm" variant="primary" icon={<UserPlus className="size-4" />} onClick={() => setCreating(true)}>
          Invite people
        </Button>
      }
    >
      {invites.isPending ? (
        <FullPageSpinner />
      ) : invites.isError ? (
        <p className="text-sm text-danger">Could not load the invites.</p>
      ) : invites.data.length === 0 ? (
        <p className="text-sm text-muted">No open invites. An invite link works once and expires after a week.</p>
      ) : (
        <ul className="-mx-2 divide-y divide-border">
          {invites.data.map((i) => (
            <li key={i.id} className="flex items-center gap-4 px-2 py-2.5 text-sm">
              <Badge tone="accent">{TEAM_ROLE_LABEL[i.role]}</Badge>
              <span className="min-w-0 flex-1 truncate text-muted">
                by {i.createdByName} · expires {formatRelative(i.expiresAt)}
              </span>
              <Tooltip label="Revoke invite">
                <Button size="icon" variant="ghost" aria-label="Revoke invite" onClick={() => revoke.mutate(i.id)}>
                  <X className="size-4" />
                </Button>
              </Tooltip>
            </li>
          ))}
        </ul>
      )}
      <CreateInviteDialog team={team} open={creating} onOpenChange={setCreating} />
    </Section>
  )
}

function CreateInviteDialog({ team, open, onOpenChange }: { team: TeamSummary; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient()
  const [role, setRole] = useState<TeamRole>('producer')
  const [created, setCreated] = useState<TeamInvite | null>(null)
  const create = useMutation({
    mutationFn: () => api<TeamInvite>(`/api/teams/${team.id}/invites`, { body: { role } }),
    onSuccess: (invite) => {
      setCreated(invite)
      qc.setQueryData(q.invites(team.id).queryKey, (list) => [...(list ?? []), { ...invite, url: undefined }])
    },
  })
  const close = (o: boolean) => {
    onOpenChange(o)
    if (!o) setCreated(null)
  }
  return (
    <Dialog open={open} onOpenChange={close} title={`Invite to ${team.name}`} description="Anyone with the link can join once. It expires after 7 days.">
      {created?.url ? (
        <div className="space-y-4">
          <p className="text-sm text-muted">Send this link to the person you want to invite. It is shown only now.</p>
          <div className="flex gap-2">
            <input readOnly value={created.url} className="input font-mono text-[13px]" onFocus={(e) => e.currentTarget.select()} aria-label="Invite link" autoFocus />
            <CopyButton text={created.url} what="Invite link" />
          </div>
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => close(false)}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <span className="field-label">Role</span>
            <Select label="Role" value={role} onChange={setRole} options={ROLE_OPTIONS} />
            <p className="mt-1.5 text-[13px] text-faint">{ROLE_HINT[role]}</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button variant="primary" loading={create.isPending} onClick={() => create.mutate()}>
              Create link
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  )
}

function RenameTeamDialog({ team, open, onOpenChange }: { team: TeamSummary; open: boolean; onOpenChange: (o: boolean) => void }) {
  const updateMeTeams = useMeTeams()
  const [name, setName] = useState(team.name)
  const rename = useMutation({
    mutationFn: (teamName: string) => api(`/api/teams/${team.id}`, { method: 'PATCH', body: { name: teamName } }),
    onSuccess: (_res, teamName) => {
      updateMeTeams((teams) => teams.map((t) => (t.id === team.id ? { ...t, name: teamName } : t)))
      onOpenChange(false)
    },
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (name.trim()) rename.mutate(name.trim())
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (o) setName(team.name)
      }}
      title="Rename team"
    >
      <form onSubmit={submit} className="space-y-6">
        <Field label="Team name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus required />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={rename.isPending} disabled={!name.trim()}>
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
